/**
 * VRG-776 — the sentence a disabled Save shows on hover.
 *
 * A separate module from `FormSubmitButton.tsx` ON PURPOSE: this is pure string work with
 * no React and no `@repo/ui` import, so the platform's guard runner — node's own test
 * runner with type stripping — can import and exercise it directly. Pulled out of the
 * component because a helper buried in a `.tsx` next to a React import is a helper nothing
 * can test, and the wording here is the part most likely to be quietly wrong.
 */

/** `scan_media_url` / `scanMediaUrl` -> "Scan media url". */
export const humanizeFieldName = (name: string): string => {
  const spaced = name
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * "Fix Name" / "Fix Name and Email" / "Fix Name, Email and 3 more fields".
 *
 * Capped at three because the tooltip is read at a glance: a form failing seven
 * validations produces a sentence nobody finishes, and the useful signal — that this is
 * validation rather than a pristine form, and roughly where — survives the cap.
 */
export const describeErrors = (
  names: readonly string[],
  labels?: Record<string, string>,
): string => {
  const shown = names.slice(0, 3).map(n => labels?.[n] ?? humanizeFieldName(n))
  const remaining = names.length - shown.length

  if (remaining > 0) {
    return `Fix ${shown.join(', ')} and ${remaining} more field${remaining === 1 ? '' : 's'}`
  }
  if (shown.length === 1) return `Fix ${shown[0]}`
  return `Fix ${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

/** What a pristine form's Save says. */
export const NO_CHANGES_REASON = 'No changes to save'

/**
 * Which sentence a blocked submit should carry, or `undefined` when it is not blocked (or
 * is blocked by a caller-supplied `disabled` with no reason given, which only the caller
 * could explain).
 *
 * Order matters. The caller's own `disabled` wins because it is usually the most specific
 * and most transient thing true about the button — a save in flight, a missing permission.
 * Validation then outranks pristine for the case where BOTH hold: a form seeded with
 * invalid stored data is untouched and invalid at once, and "no changes to save" would
 * send someone hunting for a change to make rather than for the broken field.
 */
export const submitDisabledReason = (input: {
  blocked: boolean
  callerDisabled?: boolean
  callerReason?: string
  errorNames: readonly string[]
  labels?: Record<string, string>
}): string | undefined => {
  if (!input.blocked) return undefined
  if (input.callerDisabled) return input.callerReason
  if (input.errorNames.length > 0) return describeErrors(input.errorNames, input.labels)
  return NO_CHANGES_REASON
}
